import { useCallback } from "react";
import type { DetailType } from "#shared/schemas/entityDetail.ts";
import {
  useBrowseTrailStore,
  type SheetSectionFocus,
} from "@web/stores/browseTrail.ts";
import { useDetailStore, type DetailTarget } from "@web/stores/detail.ts";
import { useUiStore } from "@web/stores/ui.ts";
import { closeDetailSheet } from "./sheetUrl.ts";

/** Open an entity in the sheet and seed/push the exploration trail. */
export function useOpenDetail(): (
  target: DetailTarget & { label?: string },
  opts?: {
    /** `reset` = new dive from table (default). `push` = in-sheet hop only. */
    mode?: "reset" | "push";
    focusCollaborators?: boolean;
    artistFocus?: "tracks" | "albums" | "playlists" | "collabs";
    /** Expand + scroll a sheet section (e.g. Ideas → samples). */
    sectionFocus?: SheetSectionFocus;
  },
) => void {
  const setTarget = useDetailStore((s) => s.setTarget);
  const setSheetOpen = useUiStore((s) => s.setSheetOpen);
  const resetTo = useBrowseTrailStore((s) => s.resetTo);
  const push = useBrowseTrailStore((s) => s.push);
  const setFocusCollaborators = useBrowseTrailStore((s) => s.setFocusCollaborators);
  const setArtistSheetFocus = useBrowseTrailStore((s) => s.setArtistSheetFocus);
  const setSheetSectionFocus = useBrowseTrailStore((s) => s.setSheetSectionFocus);

  return useCallback(
    (target, opts) => {
      const label = target.label ?? target.id;
      const entity = { type: target.type, id: target.id, label };
      if (opts?.mode === "push") push(entity);
      else resetTo(entity);

      const focus =
        opts?.artistFocus ?? (opts?.focusCollaborators ? ("collabs" as const) : null);
      if (focus) setArtistSheetFocus(focus);
      else {
        setArtistSheetFocus(null);
        setFocusCollaborators(false);
      }

      setSheetSectionFocus(opts?.sectionFocus ?? null);

      setTarget({ type: target.type, id: target.id });
      setSheetOpen(true);
    },
    [
      setTarget,
      setSheetOpen,
      resetTo,
      push,
      setFocusCollaborators,
      setArtistSheetFocus,
      setSheetSectionFocus,
    ],
  );
}

export function openSharedTracksDrawer(input: {
  artistId: string;
  artistLabel: string;
  withArtistId: string;
  withLabel: string;
}): void {
  useBrowseTrailStore.getState().openSharedTracks({
    kind: "shared_tracks",
    ...input,
  });
}

export { closeDetailSheet };

export type { DetailType };
